/**
 * API-Client.
 *
 * Alle Requests gehen via Same-Origin-Proxy (Vite dev-proxy oder Python-Proxy
 * in Production), damit der Session-Cookie funktioniert.
 */

import { buildOperationsReportQuery, type OperationsReportFilters } from "./locationReportHelpers";

export type User = {
  id: number;
  email: string;
  fullName: string;
  role: UserRole;
};

export type UserRole = "admin" | "planer" | "betrachter";

export type ManagedUser = User & {
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
};

export type UserInput = {
  email: string;
  fullName: string;
  password: string;
  role: UserRole;
  isActive: boolean;
};

export type AuditLogEntry = {
  id: number;
  userId: number | null;
  userEmail: string | null;
  action: string;
  tableName: string;
  rowId: number | string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  at: string;
};

export type ApiError = {
  statusCode?: number;
  code?: string;
  error: string;
  message: string;
  details?: unknown;
  requestId?: string;
};

class ApiCallError extends Error {
  constructor(public readonly apiError: ApiError, public readonly status: number) {
    super(apiError.message);
    this.name = "ApiCallError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(path, {
    ...options,
    credentials: "include", // Session-Cookie mitsenden
    headers: {
      Accept: "application/json",
      ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    let apiError: ApiError;
    try {
      apiError = (await res.json()) as ApiError;
    } catch {
      apiError = {
        error: "unknown",
        message: res.statusText || "Unbekannter Fehler",
      };
    }
    throw new ApiCallError(apiError, res.status);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

// ----- Domain Types -----

export type Location = {
  id: number;
  name: string;
  address: string | null;
  timezone: string;
  isActive: boolean;
  createdAt: string;
};

export type Room = {
  id: number;
  locationId: number;
  name: string;
  capacity: number;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
};

export type Resource = {
  id: number;
  locationId: number | null;
  name: string;
  category: string | null;
  serialNo: string | null;
  isActive: boolean;
  createdAt: string;
};

export type Trainer = {
  id: number;
  fullName: string;
  email: string | null;
  phone: string | null;
  specialties: string[];
  isActive: boolean;
  createdAt: string;
};

export type SessionStatus = "entwurf" | "geplant" | "abgeschlossen" | "storniert";

export type OperationsReport = {
  filters: OperationsReportFilters;
  summary: { sessions: number; participants: number; participantHours: number; cancelledSessions: number; cancellationRatePercent: number };
  roomUsage: Array<{ roomId: number; location: { id: number; name: string }; roomName: string; capacity: number; sessions: number; occupiedHours: number; participants: number; totalSeatCapacity: number; freeSeats: number; seatUtilizationPercent: number }>;
  statusTrend: Array<{ period: string; status: SessionStatus; sessions: number; cancelledSessions: number; cancellationRatePercent: number }>;
  participantsByCourse: Array<{ key: string; label: string; sessions: number; participants: number; participantHours: number }>;
  participantsByLocation: Array<{ location: { id: number; name: string }; sessions: number; participants: number; participantHours: number }>;
  participantsByTrainer: Array<{ trainerId: number; trainerName: string; sessions: number; participants: number; participantHours: number }>;
  trainerWorkload: OperationsWorkload[];
  resourceWorkload: OperationsWorkload[];
};

export type OperationsWorkload = { name: string; location: { id: number; name: string } | null; sessions: number; bookedHours: number; bookedDays: number; bottleneck: boolean; bottleneckReason: string | null };
export type OperationsCourseChoice = { key: string; courseCode: string | null; label: string; title: string | null };
export type OperationsCoursesResponse = { filters: Pick<OperationsReportFilters, "from" | "to" | "locationIds">; courses: OperationsCourseChoice[] };

export type CourseSession = {
  id: number;
  courseCode: string | null;
  title: string;
  locationId: number;
  participantCount: number;
  plannedStart: string; // ISO
  plannedEnd: string; // ISO
  status: SessionStatus;
  notes: string | null;
  seriesId: string | null;
  createdBy: number | null;
  createdAt: string;
  roomId: number | null; // aktuell zugeordneter Raum (1:1), null wenn keiner
};

export type CourseSessionWithRelations = CourseSession & {
  room: Room | null;
  trainers: Trainer[];
  resources: Resource[];
};

export type SessionSeriesInput = {
  title: string;
  courseCode?: string;
  locationId: number;
  participantCount?: number;
  notes?: string;
  weekdays: number[]; // Sonntag = 0
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  roomId?: number;
  trainerIds?: number[];
  resourceIds?: number[];
};

export type SessionSeriesResult = {
  sessions: CourseSession[];
  count: number;
  seriesId: string;
};

export type SessionAvailabilityConflict = {
  id: number;
  name: string;
  sessionId: number;
  title: string;
  plannedStart: string;
  plannedEnd: string;
};

export type SessionAvailability = {
  roomConflicts: SessionAvailabilityConflict[];
  trainerConflicts: SessionAvailabilityConflict[];
  resourceConflicts: SessionAvailabilityConflict[];
};

export type SessionAvailabilityParams = {
  plannedStart: string;
  plannedEnd: string;
  roomIds?: number[];
  trainerIds?: number[];
  resourceIds?: number[];
  excludeSessionId?: number;
};

// ----- API -----

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<{ user: User }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  me: () => request<{ user: User }>("/api/auth/me"),

  // Benutzerverwaltung (nur Administratoren)
  listUsers: () => request<{ users: ManagedUser[] }>("/api/users"),
  createUser: (body: UserInput) => request<{ user: ManagedUser }>("/api/users", {
    method: "POST",
    body: JSON.stringify(body),
  }),
  updateUser: (id: number, body: Partial<UserInput>) => request<{ user: ManagedUser }>(`/api/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }),
  listAuditLog: (params: { page: number; limit: number; userId?: number; action?: string; tableName?: string }) => {
    const query = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
    if (params.userId !== undefined) query.set("userId", String(params.userId));
    if (params.action) query.set("action", params.action);
    if (params.tableName) query.set("tableName", params.tableName);
    return request<{ auditLog: AuditLogEntry[]; page: number; limit: number }>(`/api/audit-log?${query.toString()}`);
  },

  // Locations
  listLocations: () => request<{ locations: Location[] }>("/api/locations"),
  createLocation: (body: Partial<Location>) =>
    request<{ location: Location }>("/api/locations", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateLocation: (id: number, body: Partial<Location>) =>
    request<{ location: Location }>(`/api/locations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteLocation: (id: number) =>
    request<void>(`/api/locations/${id}`, { method: "DELETE" }),

  // Rooms
  listRooms: (locationId?: number) => {
    const q = locationId ? `?locationId=${locationId}` : "";
    return request<{ rooms: Room[] }>(`/api/rooms${q}`);
  },
  createRoom: (body: Partial<Room>) =>
    request<{ room: Room }>("/api/rooms", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateRoom: (id: number, body: Partial<Room>) =>
    request<{ room: Room }>(`/api/rooms/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteRoom: (id: number) =>
    request<void>(`/api/rooms/${id}`, { method: "DELETE" }),

  // Resources
  listResources: (params?: { locationId?: number; q?: string }) => {
    const q = params
      ? "?" +
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== "")
          .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";
    return request<{ resources: Resource[] }>(`/api/resources${q}`);
  },
  createResource: (body: Partial<Resource>) =>
    request<{ resource: Resource }>("/api/resources", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateResource: (id: number, body: Partial<Resource>) =>
    request<{ resource: Resource }>(`/api/resources/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteResource: (id: number) =>
    request<void>(`/api/resources/${id}`, { method: "DELETE" }),

  // Trainers
  listTrainers: (q?: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    return request<{ trainers: Trainer[] }>(`/api/trainers${qs}`);
  },
  createTrainer: (body: Partial<Trainer> & { specialties?: string[] }) =>
    request<{ trainer: Trainer }>("/api/trainers", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateTrainer: (id: number, body: Partial<Trainer> & { specialties?: string[] }) =>
    request<{ trainer: Trainer }>(`/api/trainers/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteTrainer: (id: number) =>
    request<void>(`/api/trainers/${id}`, { method: "DELETE" }),

  // Sessions
  listSessions: (params?: { status?: string; locationId?: number; from?: string; to?: string }) => {
    const q = params
      ? "?" +
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== "")
          .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";
    return request<{ sessions: CourseSession[] }>(`/api/sessions${q}`);
  },
  getSession: (id: number) =>
    request<{ session: CourseSessionWithRelations }>(`/api/sessions/${id}`),
  getSessionAvailability: (params: SessionAvailabilityParams) => {
    const query = new URLSearchParams({
      plannedStart: params.plannedStart,
      plannedEnd: params.plannedEnd,
    });
    if (params.roomIds?.length) query.set("roomIds", params.roomIds.join(","));
    if (params.trainerIds?.length) query.set("trainerIds", params.trainerIds.join(","));
    if (params.resourceIds?.length) query.set("resourceIds", params.resourceIds.join(","));
    if (params.excludeSessionId !== undefined) query.set("excludeSessionId", String(params.excludeSessionId));
    return request<SessionAvailability>(`/api/sessions/availability?${query.toString()}`);
  },
  createSession: (body: Partial<CourseSession>) =>
    request<{ session: CourseSession }>("/api/sessions", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  createSessionSeries: (body: SessionSeriesInput) =>
    request<SessionSeriesResult>("/api/sessions/series", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateSession: (id: number, body: Partial<CourseSession>) =>
    request<{ session: CourseSession }>(`/api/sessions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  moveSession: (id: number, body: { locationId: number; roomId: number | null }) =>
    request<{ session: CourseSession }>(`/api/sessions/${id}/move`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  updateSessionSchedule: (id: number, body: { plannedStart: string; plannedEnd: string; roomId?: number | null }) =>
    request<{ session: CourseSession }>(`/api/sessions/${id}/schedule`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  assignSessionRoom: (sessionId: number, roomId: number | null) =>
    request<{ session: CourseSession; room: Room | null }>(
      `/api/sessions/${sessionId}/rooms`,
      { method: "PATCH", body: JSON.stringify({ roomId }) }
    ),
  assignSessionTrainers: (sessionId: number, trainerIds: number[], confirmedUnderTravelWarning = false) =>
    request(`/api/sessions/${sessionId}/trainers`, {
      method: "PATCH",
      body: JSON.stringify({ trainerIds, confirmedUnderTravelWarning }),
    }),
  assignSessionResources: (sessionId: number, resourceIds: number[]) =>
    request(`/api/sessions/${sessionId}/resources`, {
      method: "PATCH",
      body: JSON.stringify({ resourceIds }),
    }),
  deleteSessionSeries: (seriesId: string) =>
    request<{ deletedCount: number }>(`/api/sessions/series/${encodeURIComponent(seriesId)}`, { method: "DELETE" }),
  deleteSession: (id: number) =>
    request<void>(`/api/sessions/${id}`, { method: "DELETE" }),

  // Reports
  getOperationsReport: (filters: OperationsReportFilters) =>
    request<OperationsReport>(`/api/reports/operations?${buildOperationsReportQuery(filters).toString()}`),
  listOperationsCourses: (filters: Pick<OperationsReportFilters, "from" | "to" | "locationIds">) => {
    const query = new URLSearchParams({ from: filters.from, to: filters.to });
    if (filters.locationIds.length) query.set("locationIds", filters.locationIds.join(","));
    return request<OperationsCoursesResponse>(`/api/reports/operations/courses?${query.toString()}`);
  },
};

export { ApiCallError };
